import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, RotateCcw } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface ReopenEstimateDialogProps {
    open: boolean;
    onClose: () => void;
    estimateId: string;
    /** Shown in the dialog body for context (e.g. "Estimate #E-1042"). */
    estimateLabel?: string | null;
    /** Whether the customer approved online — tunes the wording. */
    approvedOnline?: boolean;
    /** The status being reopened from — decides whether this reads as
     *  "correct a mistake" (accepted) or "re-quote a lost sale" (declined). */
    priorStatus?: string;
    /** Called after a successful reopen so the caller can refresh. */
    onReopened?: () => void;
}

/**
 * Reopen-to-correct dialog (Issue #570). An issued estimate freezes by design
 * (#34); this is the governed correction door — admin/finance only, reason
 * required. The server snapshots the approved version to
 * `estimate_reopen_snapshots`, drops the estimate to `draft`, clears the
 * approval, and audits the reopen. Because the customer approved the old
 * numbers, the office re-sends afterward and the customer re-approves the
 * corrected total.
 */
export function ReopenEstimateDialog({
    open,
    onClose,
    estimateId,
    estimateLabel,
    approvedOnline,
    priorStatus,
    onReopened,
}: ReopenEstimateDialogProps) {
    const qc = useQueryClient();
    const [reason, setReason] = useState("");
    // A declined estimate is a lost sale being re-quoted, not a mistake being
    // corrected — same mechanism, opposite story (#1124).
    const isRequote = priorStatus === "declined";

    const reopen = useMutation({
        mutationFn: () => api.post(`/api/estimates/${estimateId}/reopen`, { reason: reason.trim() }),
        onSuccess: () => {
            toast.success(
                isRequote
                    ? "Ready to re-quote — change the numbers, then re-send it"
                    : "Estimate reopened — fix the lines, then re-send for approval",
            );
            qc.invalidateQueries({ queryKey: ["estimate", estimateId] });
            qc.invalidateQueries({ queryKey: ["commercial"] });
            setReason("");
            onReopened?.();
            onClose();
        },
        onError: (err: any) => {
            const msg = err?.body?.message ?? err?.body?.error ?? err?.message ?? "unknown error";
            toast.error(`Reopen failed: ${msg}`);
        },
    });

    const canSubmit = reason.trim().length > 0 && !reopen.isPending;

    return (
        <Dialog open={open} onClose={onClose} title={isRequote ? "Re-quote this estimate" : "Reopen to correct"}>
            <div className="space-y-4">
                {isRequote ? (
                    <p className="text-sm text-foreground/80">
                        Take another run at{" "}
                        {estimateLabel ? (
                            <span className="font-mono font-medium">{estimateLabel}</span>
                        ) : (
                            "this estimate"
                        )}
                        ? It drops back to an editable <span className="font-medium">draft</span> so you can change the
                        numbers. What the customer saw is kept for the record, and the reopen is logged.
                    </p>
                ) : (
                    <p className="text-sm text-foreground/80">
                        Reopen{" "}
                        {estimateLabel ? (
                            <span className="font-mono font-medium">{estimateLabel}</span>
                        ) : (
                            "this estimate"
                        )}{" "}
                        to fix a mistake? It drops back to an editable <span className="font-medium">draft</span>. The
                        version the customer {approvedOnline ? "approved online" : "accepted"} is snapshotted for the
                        record, and the reopen is logged.
                    </p>
                )}
                <p className="rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                    {isRequote ? (
                        <>
                            Nothing goes back to the customer until you <span className="font-semibold">re-send</span>{" "}
                            it — their original link keeps working.
                        </>
                    ) : (
                        <>
                            Reopening clears the current approval — you&apos;ll need to{" "}
                            <span className="font-semibold">re-send</span> so the customer approves the corrected total.
                        </>
                    )}
                </p>
                <div className="space-y-1.5">
                    <Label htmlFor="reopen-reason">Reason *</Label>
                    <Textarea
                        id="reopen-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={
                            isRequote
                                ? "What are you changing? (e.g. dropped the after-hours rate)"
                                : "What needs correcting on this estimate?"
                        }
                        rows={3}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={reopen.isPending}>
                        {isRequote ? "Cancel" : "Keep Locked"}
                    </Button>
                    <Button disabled={!canSubmit} onClick={() => reopen.mutate()}>
                        {reopen.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <RotateCcw className="h-4 w-4 mr-1" />
                        )}
                        {isRequote ? "Re-quote" : "Reopen Estimate"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
